// ABOUTME: Renders the paginated list of articles in the citation pane.
// ABOUTME: Uses an IntersectionObserver sentinel to trigger load-more when user scrolls near the end.

import React, { useEffect, useRef, useState } from 'react';
import { CitationItem } from '../../components/CitationItem';
import { ArticleDetailDialog } from '../../components/ArticleDetailDialog';
import '../../styles/citemed-theme.css';

export const ArticleList = ({
    loading,
    loadingMore,
    search,
    articles,
    selectedArticles,
    setSelectedArticles,
    hasMore,
    onLoadMore,
}) => {
    const [detailDialog, setDetailDialog] = useState({ open: false, article: null });
    const sentinelRef = useRef(null);

    const toggleValue = (value) => {
        setSelectedArticles((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    const handleCloseDetailDialog = () => {
        setDetailDialog({ open: false, article: null });
    };

    useEffect(() => {
        if (!hasMore || !onLoadMore) return;
        const node = sentinelRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some(e => e.isIntersecting)) {
                    onLoadMore();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, onLoadMore, articles.length]);

    const styles = {
        list: {
            flex: 1,
            listStyleType: 'none',
            padding: '8px',
            margin: 0,
            backgroundColor: 'var(--Gray-25)',
            overflowY: 'auto',
        },
        noResult: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: 'white',
        },
        noResultTitle: {
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--Gray-900)',
            marginBottom: '8px',
        },
        noResultText: {
            fontSize: '14px',
            lineHeight: '20px',
            color: 'var(--Gray-600)',
            maxWidth: '320px',
        },
        emptyIcon: {
            width: '48px',
            height: '48px',
            marginBottom: '16px',
            color: 'var(--Gray-300)',
        },
        sentinel: {
            height: '1px',
        },
        loadMoreRow: {
            textAlign: 'center',
            padding: '8px 0',
            fontSize: '12px',
            color: 'var(--Gray-600)',
        },
    };

    if (!loading && articles.length === 0) {
        const noResultTitle = search
            ? `No results found for "${search}"`
            : 'No articles match the current filters';
        const noResultText = search
            ? "We couldn't find any matches. Try checking your spelling, using fewer keywords, or searching for related terms."
            : 'Try adjusting the status or tag filters to see more articles.';

        return (
            <div style={styles.noResult}>
                <svg style={styles.emptyIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <div style={styles.noResultTitle}>{noResultTitle}</div>
                <div style={styles.noResultText}>{noResultText}</div>
            </div>
        );
    }

    return (
        <ul style={styles.list}>
            {articles.map((article) => (
                <CitationItem
                    key={article.id}
                    {...article}
                    selected={selectedArticles}
                    onClickItem={() => setDetailDialog({ open: true, article })}
                    onCheckItem={toggleValue}
                />
            ))}
            {hasMore && <li ref={sentinelRef} style={styles.sentinel} aria-hidden="true" />}
            {loadingMore && <li style={styles.loadMoreRow}>Loading more…</li>}
            <ArticleDetailDialog {...detailDialog} onClose={handleCloseDetailDialog} />
        </ul>
    );
};
